import Box from "@mui/material/Box";
import ReactECharts from "echarts-for-react";
import type { ECElementEvent, ECharts, EChartsOption } from "echarts";

const ECHARTS_RENDERER_OPTIONS = { renderer: "canvas" } as const;

interface EChartsRendererProps {
  option: EChartsOption;
  ariaLabel: string;
  height?: number | string;
  testId?: string;
  /** Merge new series data into existing chart state instead of a full replace (defaults to full replace). */
  notMerge?: boolean;
  /** Components that ECharts must replace instead of retaining through merge. */
  replaceMerge?: string | string[];
  /**
   * Whether to defer applying `setOption` to the next animation frame (defaults to `true`).
   * Batching like this is good for performance when a chart updates very frequently, but it
   * can make user-driven interactions (e.g. clicking a legend item to toggle a series) feel
   * unresponsive if those updates land in the same frame as an unrelated, frequent background
   * `setOption` call (e.g. a live-refreshing chart). Set this to `false` for charts that both
   * refresh continuously and expose click-driven interactions like a legend.
   */
  lazyUpdate?: boolean;
  /**
   * ECharts event handlers (e.g. `{ click: handler }`). When provided, the chart is
   * treated as interactive: the wrapper drops `role="img"`/`aria-hidden` in favor of a
   * plain section, since role="img" implies non-interactive content.
   */
  onEvents?: Record<string, (params: ECElementEvent) => void>;
  /** Invoked with the live ECharts instance once it's initialized. */
  onChartReady?: (instance: ECharts) => void;
}

export function EChartsRenderer({
  option,
  ariaLabel,
  height = 240,
  testId,
  notMerge = true,
  replaceMerge,
  lazyUpdate = true,
  onEvents,
  onChartReady,
}: EChartsRendererProps) {
  const interactive = onEvents != null;

  return (
    <Box
      component={interactive ? "section" : "div"}
      role={interactive ? undefined : "img"}
      aria-label={ariaLabel}
      data-testid={testId}
      sx={{ width: "100%", height, minHeight: 220 }}
    >
      <ReactECharts
        aria-hidden={interactive ? undefined : "true"}
        option={option}
        notMerge={notMerge}
        replaceMerge={replaceMerge}
        lazyUpdate={lazyUpdate}
        opts={ECHARTS_RENDERER_OPTIONS}
        onEvents={onEvents}
        onChartReady={onChartReady}
        style={{ width: "100%", height: "100%" }}
      />
    </Box>
  );
}
